import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";
import { useHistory } from "react-router-dom";
import { getFileUrl } from "../../core/config/api";
import { FeatureGatingContext, SettingsContext, UserContext, useEntitlement } from "../../contexts";
import { useStoreWriteGuard } from "../../helpers/storeScope";
import {
  ch24PublishPresentation,
  ch24ShouldFetchState,
} from "../../helpers/chrono24PublishState";
// useListingList intentionally not imported — per-card toggles must not
// mutate page-level filters (see onDaoChange / onConsignmentChange below).
import { ShareHelper } from "../../helpers";
import { buildListingBroadcastMessage } from "../helpers";
import EbayPolicyModal from "./EbayPolicyModal";
import DuplicateListingModal from "./DuplicateListingModal";
import { marketplaceIconSrc } from "./MarketplaceDonut";
import {
  useListingMarketplaceState,
  useMarketplaceAccounts,
} from "../../hooks/marketplaceAccounts";
import { useActiveStoreIdM } from "../../core/hooks/useActiveStoreIdM";
import { isInRepair } from "../../listingFlows/intakeApi";
import ListingInternalFace, { hasInternalFace } from "./ListingInternalFace.jsx";
import {
  Badge,
  Icon,
  IconButton,
  ListingCard,
  ListingCardCell,
  Menu,
  Toggle,
  Tooltip,
} from "../../components/ui";
import { useSendToWhatsApp, listingSubject } from "../../waBroadcast";
import { storeCaption } from "../../waBroadcast/waBroadcastApi";
import PriceCheckChips from "../priceCheck/PriceCheckChips";
import "./rc-listing-card.scss";

const MarketplaceIcon = ({ marketplace, label: _label }) => (
  <span className="rc-lcard-adapter__mp-icon-wrap">
    <img
      src={marketplaceIconSrc(marketplace)}
      alt=""
      className="rc-lcard-adapter__mp-icon"
      aria-hidden="true"
    />
  </span>
);

const MARKETPLACE_SORT = { ebay: 0, chrono24: 1, watchfinder: 2 };

const sortEnabledMpAccounts = (accounts) =>
  [...accounts].sort((a, b) => {
    const orderA = MARKETPLACE_SORT[a.marketplace] ?? 9;
    const orderB = MARKETPLACE_SORT[b.marketplace] ?? 9;
    if (orderA !== orderB) return orderA - orderB;
    if (a.marketplace === "chrono24" && b.marketplace === "chrono24") {
      if (a.slug === null && b.slug !== null) return -1;
      if (b.slug === null && a.slug !== null) return 1;
    }
    return (a.label || "").localeCompare(b.label || "");
  });

const marketplaceReviewLabel = (isListed, syncStatus) => {
  if (!isListed) return null;
  if (syncStatus === "synced" || syncStatus === "pending" || syncStatus === "importing") {
    return "To Be Reviewed";
  }
  return null;
};

// Hoisted so the Chrono24ToggleRow child component (defined below) can use it.
// Mark axios calls as silent so the global 422 interceptor in contexts/User.jsx
// skips its generic toast — the component's own catch shows the precise error.
const SILENT_AXIOS = { metadata: { silent: true } };

/** Marketplace-panel toggle — shared Toggle with the live controller state. */
const TOGGLE_TONES = ["active", "reserved", "pending"];
const RCToggle = ({ checked, onChange, variant, title, disabled = false }) => (
  <Toggle
    bare
    size="md"
    checked={checked}
    onChange={onChange}
    title={title}
    disabled={disabled}
    tone={TOGGLE_TONES.includes(variant) ? variant : "default"}
  />
);

const RCListingCard = ({ listing, onDeleted, priceReviewMode = false }) => {
  const history = useHistory();
  const alert = useAlert();
  const { showApiError } = useApiError();
  const { checkFeatureQuota } = useContext(FeatureGatingContext);
  // Share to Broadcast (Feature 16).
  const canShare = useEntitlement("share_to_broadcast");
  const { settings } = useContext(SettingsContext);
  // activeStoreId is needed for the internal-face ownership check — the API's
  // own gate compares against the ACTIVE store, not the user's home store.
  const { user, activeStoreId } = useContext(UserContext);
  // Per-card toggles intentionally do NOT touch useListingList's page-level
  // filters — see onDaoChange / onConsignmentChange. The destructure is kept
  // empty here so we don't accidentally re-introduce filter mutations.
  // Card flip — only the TOP region (info + photo) rotates, so the card never
  // changes height and the status/marketplace controls below stay put.
  const [flipped, setFlipped] = useState(false);

  const product = listing?.product;
  const [active, setActive] = useState(listing?.status === "active");
  // Sold listings can't be reactivated in place — flipping Active ON opens the
  // "duplicate this listing?" prompt instead (see onActiveChange).
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [reserved, setReserved] = useState(!!listing?.is_reserved);
  const [displayOpen, setDisplayOpen] = useState(false);

  // The marketplace panel renders inside the card, so it needs its own dismissal: Escape, or a
  // click anywhere that is not the panel or the footer button. Both of those stop the click from
  // reaching the document, which is also what keeps the button a real toggle — a document-level
  // "close on any click" would otherwise close on mousedown and the button would reopen on click.
  useEffect(() => {
    if (!displayOpen) return undefined;
    const close = () => setDisplayOpen(false);
    const onKey = (event) => { if (event.key === "Escape") close(); };
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [displayOpen]);

  const [ebay, setEbay] = useState(!!listing?.is_ebay);
  const [watchfinder, setWatchfinder] = useState(!!listing?.is_wf);

  // Chrono24 is now multi-account (one toggle per StoreMarketplaceAccount).
  // Fetch the enabled chrono24 accounts for this listing's store; today only
  // DAO-UK (slug=null) is enabled by default, so the UI renders identically.
  // When a merchant enables additional accounts (FR / DXB / etc.), they appear
  // here automatically and the n/total denominator grows.
  // Prefer the listing's own store, then the active/home store via
  // useActiveStoreIdM. Only an RC master with no store selected falls back to
  // the DAO env default; anyone else with no store gets null and no accounts
  // query, so a non-DAO card never queries DAO's marketplace accounts (L12).
  const fallbackStoreIdM = useActiveStoreIdM();
  const storeIdM = listing?.store_id_m || fallbackStoreIdM;
  // WatchFinder publish-status pill: find the store's enabled WatchFinder
  // account, then read this listing's sync state (only when toggled on, to
  // avoid creating empty junction rows for listings not on WatchFinder).
  const { accounts: allMpAccounts, status: mpAccountsStatus } = useMarketplaceAccounts(storeIdM);
  const wfAccount = (allMpAccounts || []).find(
    (a) => a.marketplace === "watchfinder" && a.is_enabled
  );

  const mpAccountsLoading = mpAccountsStatus === "loading";
  const enabledMpAccounts = useMemo(
    () => sortEnabledMpAccounts((allMpAccounts || []).filter((a) => a.is_enabled)),
    [allMpAccounts]
  );

  const { state: wfState } = useListingMarketplaceState(listing?.id, wfAccount?.id, {
    enabled: !!wfAccount?.id && !!listing?.is_wf,
  });
  const WF_PILL = {
    published: { label: "Published", tone: "success" },
    synced:    { label: "In review", tone: "warning" },
    importing: { label: "Syncing",   tone: "info" },
    pending:   { label: "Queued",    tone: "warning" },
    error:     { label: "Error",     tone: "danger" },
  };
  const wfPill = listing?.is_wf && wfState ? WF_PILL[wfState.sync_status] : null;
  // WatchFinder stores rejection reasons in Mirakl's raw format:
  //   "1000|<message>,1000|<message>" (1000 = Mirakl error code, entries
  // comma-joined). Strip the codes and split into readable lines. Split on a
  // comma only when it precedes a "<code>|" so messages containing commas
  // survive.
  // The code prefix is NOT always numeric. Mirakl mixes numeric attribute codes
  // with named ones: "1000|The attribute 'serialNumber' is required" but also
  // "product-mapping|Product does not map to Watchfinder Catalogue" and
  // "general-question|Further clarification required". Matching only \d+ left
  // the named prefixes rendered raw in the pill (prod 2026-09-02, listings 2709
  // and 3560). [a-z0-9-]+ covers both.
  const parseWfReasons = (raw) => String(raw || "")
    .split(/,(?=[a-z0-9-]+\|)/i)
    .map((e) => e.replace(/^\s*[a-z0-9-]+\|/i, "").trim())
    .filter(Boolean);

  // A listing can carry TWO rejections — one for the product (catalogue entry)
  // and one for the offer (price). Show the PRODUCT one when present: an
  // offer only fails because its product has not been accepted, so the offer
  // message is "The product does not exist" — which reads as a flat
  // contradiction next to a product plainly visible in WatchFinder's catalogue
  // (it confused us for a full round of debugging on 2026-10-02). When the
  // product is fine, the offer error IS the actionable one, so fall back to it.
  const wfProductReasons = parseWfReasons(wfState?.last_error);
  const wfErrorReason = wfProductReasons.length
    ? wfProductReasons
    : parseWfReasons(wfState?.offer_last_error);

  // The red WF badge has exactly TWO causes, and the tip names which one:
  //
  //   A. toggle OFF  — an active listing nobody has published to WatchFinder.
  //      wfState is not even fetched in this case (the hook is gated on
  //      listing.is_wf), so there is no reason to show and never will be.
  //   B. toggle ON + sync_status 'error' — WatchFinder refused it. Show their
  //      words, and say what it means: the watch is not on sale there.
  //
  // Rendered through the design-system Tooltip rather than a native `title`:
  // the badge sits inside the photo overlay, where a `title` only ever reaches
  // a hovering mouse — never a keyboard, never touch.
  const wfNotPublishedCopy =
    "This listing is active but its WatchFinder toggle is off, so it is not published there.";
  const wfNoReasonCopy =
    "WatchFinder refused this listing but did not say why. It is not on sale there.";

  const wfBadgeTipText = !watchfinder
    ? wfNotPublishedCopy
    : (wfErrorReason.length
        ? `WatchFinder refused this listing, so it is not on sale there: ${wfErrorReason.join(" ")}`
        : wfNoReasonCopy);

  const wfBadgeTip = watchfinder && wfErrorReason.length ? (
    <span className="rc-lcard-adapter__wf-tip">
      <strong>Not on sale on WatchFinder</strong>
      {wfErrorReason.map((reason) => (
        <span key={reason} className="rc-lcard-adapter__wf-tip-reason">{reason}</span>
      ))}
      <span className="rc-lcard-adapter__wf-tip-hint">
        Fix it on the listing and save — it is resent automatically.
      </span>
    </span>
  ) : wfBadgeTipText;

  // Per-account on/off state, reported up by each Chrono24ToggleRow child.
  // The child is responsible for fetching its initial value (legacy DAO-UK
  // reads listing.is_chrono; non-legacy fetches via the per-account API),
  // optimistic updates, and rollback on failure. The parent only needs the
  // current state for the marketplaceCount denominator.
  const [chronoState, setChronoState] = useState({});
  const [chronoSyncState, setChronoSyncState] = useState({});
  const reportChronoState = useCallback((accountId, on) => {
    setChronoState((prev) => (prev[accountId] === on ? prev : { ...prev, [accountId]: on }));
  }, []);
  const reportChronoHydration = useCallback((accountId, state) => {
    if (!state) return;
    setChronoState((prev) =>
      prev[accountId] === !!state.is_listed ? prev : { ...prev, [accountId]: !!state.is_listed }
    );
    setChronoSyncState((prev) =>
      prev[accountId] === state.sync_status ? prev : { ...prev, [accountId]: state.sync_status }
    );
  }, []);
  const [internalProduct, setInternalProduct] = useState(listing?.internal_product || "no");
  const [ebayPolicyFlagged, setEbayPolicyFlagged] = useState(!!listing?.ebay_policy_flagged);
  const [ebayWarnVariant, setEbayWarnVariant] = useState(null);

  // Single source of truth for both per-card toggles is `internal_product`:
  //   "owned"           → In Stock toggle ON
  //   "exclusive"       → Consignment toggle ON
  //   "no"              → both OFF
  //   "to_be_procured"  → both OFF *and locked* (see below)
  // Because they share one field, mutual exclusivity is automatic and a
  // single API call updates both states.
  const inStock = internalProduct === "owned";
  const consignment = internalProduct === "exclusive";
  // Owned By has a fourth value these two toggles cannot express. Left live,
  // either of them would write "owned"/"exclusive"/"no" over it on a single
  // click — a silent data loss on a listing nobody meant to reclassify. So the
  // pair goes read-only and says what the real value is; changing it is a
  // deliberate act in the listing form's Owned By dropdown.
  const ownershipLocked = internalProduct === "to_be_procured";
  const ownershipLockNote =
    "Owned By is “To Be Procured” — change it from the listing's Owned By dropdown.";
  // "In Repair" — the listings.is_repair flag, set by CREATE REPAIR on the New
  // Stock / New Consignment flows and cleared by the toggle under Internal
  // Details ▸ Cost. isInRepair also honours the "[In Repair]" note marker used
  // before that column existed, so older repairs keep their badge.
  const inRepair = isInRepair(listing);

  // Toggle state is the source of truth for the marketplace count — reading
  // listing.marketplace_count would show the stale backend value on first paint.
  // Total = 2 fixed (eBay + Watchfinder) + N enabled Chrono24 accounts for the
  // listing's store. The "n/total" display grows automatically when a merchant
  // enables a new Chrono24 account in Master Admin (e.g. Chrono24 France).
  const marketplaceCount = enabledMpAccounts.reduce((count, acc) => {
    if (acc.marketplace === "ebay") return count + (ebay ? 1 : 0);
    if (acc.marketplace === "watchfinder") return count + (watchfinder ? 1 : 0);
    if (acc.marketplace === "chrono24") return count + (chronoState[acc.id] ? 1 : 0);
    return count;
  }, 0);
  // NO Math.max floor: a store with zero marketplace accounts must read
  // "0/0" (count hidden below), not the contradictory "0/1" next to a
  // "No marketplace accounts in Settings" note (staging test 2026-07-31,
  // Listings Q2).
  const marketplaceTotal = enabledMpAccounts.length;

  // Seed legacy Chrono24 toggle state so the count is correct before rows paint.
  useEffect(() => {
    enabledMpAccounts.forEach((acc) => {
      if (acc.marketplace === "chrono24" && acc.slug === null) {
        reportChronoState(acc.id, !!listing?.is_chrono);
      }
    });
  }, [listing?.id, listing?.is_chrono, enabledMpAccounts, reportChronoState]);

  // No literal fallback path: `/assets/placeholder-watch.png` doesn't exist in
  // public/, so it 404'd to index.html (wrong content-type). ListingCard
  // already renders the empty photo well when `image` is falsy.
  const image =
    getFileUrl(listing?.image_id) ||
    getFileUrl(product?.images?.[0]?.id);

  const brand = product?.brand?.name || (typeof product?.brand === "string" ? product.brand : null);
  const ref   = product?.reference_number || null;
  const model = product?.model?.name  || (typeof product?.model  === "string" ? product.model  : "—");
  const year      = listing?.year || product?.year || null;
  const conditionRaw = listing?.condition || product?.condition || null;
  const condition = conditionRaw ? conditionRaw.charAt(0).toUpperCase() + conditionRaw.slice(1) : null;
  // Backend stores prices in pence — divide by 100 for display.
  const priceVal = listing?.listing_price || listing?.price || listing?.sell_price || null;
  // Sell-price currency now lives on the listing row (NULL = legacy GBP).
  const price = priceVal ? Number(priceVal) / 100 : null;
  const currency = listing?.currency || "gbp";
  // Raw search rows carry the tinyint uncast (1 / "1"); GET /listings casts it.
  const needsReview = listing?.needs_review === true || String(listing?.needs_review) === "1";

  const onActiveChange = async (val) => {
    // A sold listing may not be reactivated — prompt to duplicate instead.
    if (val && listing.is_sold) {
      setShowDuplicate(true);
      return undefined;
    }
    setActive(val);
    try {
      await axios.put(`/api/listings/${listing.id}/active`, { active: val }, SILENT_AXIOS);
    } catch (error) {
      setActive(!val);
      showApiError(error, "update the listing’s active state");
    }
    return undefined;
  };

  const onDuplicateYes = async () => {
    setDuplicating(true);
    try {
      await axios.post(`/api/listings/${listing.id}/duplicate`);
      setShowDuplicate(false);
      alert.success(
        "Listing duplicated as an inactive draft. Turn off the Sold filter to find it."
      );
    } catch (err) {
      showApiError(err, "duplicate the listing");
    } finally {
      setDuplicating(false);
    }
  };

  const onDuplicateNo = () => {
    if (duplicating) return;
    setShowDuplicate(false);
  };

  const SILENT = SILENT_AXIOS;

  const onReservedChange = async (val) => {
    setReserved(val);
    try {
      await axios.put(`/api/listings/${listing.id}/reserved`, {
        reserved: val,
        reserved_for: null,
      }, SILENT);
    } catch (error) {
      setReserved(!val);
      showApiError(error, "update the listing’s reserved state");
    }
  };

  // Per-card toggles update ONLY this listing's `internal_product` field — no
  // page-level filter mutation, no separate /consignment call. Because In Stock
  // and Consignment share one field ("owned" / "exclusive" / "no"), turning
  // one ON automatically turns the other OFF in a single PUT.
  const setInternalProductValue = async (next, fallbackPhrase) => {
    const prev = internalProduct;
    setInternalProduct(next);
    try {
      await axios.put(
        `/api/listings/${listing.id}/internal_product`,
        { internal_product: next },
        SILENT
      );
    } catch (error) {
      setInternalProduct(prev);
      showApiError(error, fallbackPhrase);
    }
  };

  // Both refuse to act on a value they cannot express — the disabled toggles
  // above are the visible half of the same rule, this is the one that actually
  // protects the field.
  const onDaoChange = (val) => {
    if (ownershipLocked) return;
    setInternalProductValue(val ? "owned" : "no", "update In Stock");
  };

  const onConsignmentChange = (val) => {
    if (ownershipLocked) return;
    setInternalProductValue(val ? "exclusive" : "no", "update Consignment");
  };

  // Collapse a Laravel validation payload ({errors: {field: ["msg",...]}})
  // into a single readable string so toggle errors surface missing fields.
  const formatApiError = (error, fallback) => {
    const data = error?.response?.data;
    if (data?.errors && typeof data.errors === "object") {
      const lines = Object.values(data.errors).flat().filter(Boolean);
      if (lines.length) return lines.join(" · ");
    }
    if (data?.error) return data.error;
    if (data?.message && data.message !== "The given data was invalid.") {
      return data.message;
    }
    return fallback;
  };

  // ── eBay counterfeit / policy-restriction warning ──
  // Two flags drive the popup:
  //   - listing.ebay_policy_flagged → eBay killed THIS listing previously
  //   - settings.ebay_restricted_until > now → seller account is banned for 10d
  // Variant A = listing only, B = account only, C = both. See EbayPolicyModal.
  const ebayRestrictedUntil = settings?.ebay_restricted_until || null;
  const accountRestricted =
    ebayRestrictedUntil && new Date(ebayRestrictedUntil).getTime() > Date.now();

  const performEbayPublish = async (val) => {
    setEbay(val);
    try {
      await axios.put(`/api/listings/${listing.id}/ebay`, { is_ebay: val }, SILENT);
    } catch (error) {
      setEbay(!val);
      // 25019 means we just tripped the policy block — flip the local flag so
      // the next click warns immediately without waiting for a refetch.
      // The raw text is read here to detect the policy block, never to display.
      const raw = formatApiError(error, "");
      if (/eBay publish failed/i.test(raw)) {
        setEbayPolicyFlagged(true);
      }
      showApiError(error, "update the eBay listing");
    }
  };

  const onEbayChange = async (val) => {
    if (!val) {
      // Turning OFF doesn't risk a ban — skip the warning.
      performEbayPublish(val);
      return;
    }
    let variant = null;
    if (ebayPolicyFlagged && accountRestricted) variant = "C";
    else if (accountRestricted)                  variant = "B";
    else if (ebayPolicyFlagged)                  variant = "A";
    if (variant) {
      setEbayWarnVariant(variant);
      return;
    }
    performEbayPublish(val);
  };

  const onEbayWarnConfirm = () => {
    setEbayWarnVariant(null);
    performEbayPublish(true);
  };

  const onEbayWarnCancel = () => {
    setEbayWarnVariant(null);
  };

  // Manual override from Actions menu — flips the per-listing flag both ways.
  const onToggleEbayFlag = async (e) => {
    e?.stopPropagation?.();
    const next = !ebayPolicyFlagged;
    const previous = ebayPolicyFlagged;
    setEbayPolicyFlagged(next);
    try {
      await axios.put(
        `/api/listings/${listing.id}/ebay-policy-flag`,
        { flagged: next },
        SILENT
      );
    } catch (error) {
      setEbayPolicyFlagged(previous);
      showApiError(error, "update the eBay flag");
    }
  };

  // onChronoChange now lives inside Chrono24ToggleRow (see end of file).

  const onWatchfinderChange = async (val) => {
    // WatchFinder won't accept an offer at or below £100 — block turning it on
    // until a valid price is set on the listing's WatchFinder tab. wf_price is
    // stored in pence, so divide back to GBP for the check.
    if (val) {
      const priceGbp = (listing.wf_price ?? 0) / 100;
      if (!(priceGbp > 100)) {
        alert.show(
          "Set a WatchFinder price above £100 (in the listing's WatchFinder tab) before publishing."
        );
        return;
      }
      // WatchFinder requires a serial number to create the product.
      if (!String(listing.serial_number ?? "").trim()) {
        alert.show(
          "Add the serial number (Internal Details) before publishing to WatchFinder."
        );
        return;
      }
      // Bracelet size is a mandatory WatchFinder key.
      if (!String(listing.wf_bracelet_size ?? "").trim()) {
        alert.show(
          "Add the bracelet size (listing's WatchFinder tab) before publishing to WatchFinder."
        );
        return;
      }
      // WF price must not sit below the DAO price, nor below the buying price
      // (buying price is ignored when 0 / empty). All values are in pence.
      const wf = Number(listing.wf_price ?? 0);
      const dao = Number(listing.price ?? 0);
      if (wf < dao) {
        alert.show(`WatchFinder price is below the ${settings?.name || "store"} price.`);
        return;
      }
      const buying = Number(listing.buying_price ?? 0);
      if (buying > 0 && wf < buying) {
        alert.show("WatchFinder price is below the buying price.");
        return;
      }
    }
    setWatchfinder(val);
    try {
      // is_wf is decoupled from wf_url now — just flip the toggle.
      await axios.put(`/api/listings/${listing.id}/watchfinder`, {
        is_wf: val ? 1 : 0,
      }, SILENT);
    } catch (error) {
      setWatchfinder(!val);
      showApiError(error, "update the Watchfinder listing");
    }
  };

  // Master admins on "Master Store" are unscoped, so an edit has no single
  // store to save against — the control is disabled with an explanation
  // rather than allowed to write against the RC master store.
  const { blocked: storeGuardBlocked, reason: storeGuardReason } = useStoreWriteGuard();
  // WhatsApp Broadcast (master admins, DAO listings): post to dealer groups.
  const wa = useSendToWhatsApp();

  const onEdit = (e) => {
    e?.stopPropagation?.();
    window.open(`/product/${listing.id}/edit/product`, "_blank");
  };

  const onNavigate = (e) => {
    e?.stopPropagation?.();
    history.push(`/product/${listing.id}`);
  };

  const onBroadcast = async (e) => {
    e?.stopPropagation?.();
    const allowed = await checkFeatureQuota(FeatureGatingContext.features.SHARE_WHATSAPP);
    if (!allowed) return;
    // The store's own template when it has one (Owner Admin → Message templates).
    ShareHelper.whatsapp(await storeCaption("listing", listing.id, buildListingBroadcastMessage(listing)));
  };

  const onDelete = async (e) => {
    e?.stopPropagation?.();
    if (!confirm("Are you sure you want to delete this listing?")) return;
    let res;
    try {
      res = await axios.delete(`/api/listings/${listing.id}`);
    } catch {
      // The interceptor has already told the user; the listing still exists.
      return;
    }
    // A 5xx RESOLVES undefined (global interceptor, which already showed the
    // error): keep the card — the listing was NOT deleted.
    if (!res) return;
    onDeleted && onDeleted(listing.id);
  };

  // Record Sale — opens the purpose-built "Record a sale" flow with this
  // listing pre-selected. Route state is the existing contract for entering a
  // request flow with a listing in hand (see PartExchangeButton + the
  // location.state.listing branch of useRequest.loadInitialState).
  const onRecordSale = (e) => {
    e?.stopPropagation?.();
    history.push(
      `/request/record-sale${settings?.iframeQueryParams ? `?${settings.iframeQueryParams}` : ""}`,
      { listing }
    );
  };

  // The internal face only exists for an admin OF THE STORE that owns the
  // listing — same predicate the API uses. No owner → no flip affordance at
  // all, rather than a back face of "—".
  const canFlip = hasInternalFace(user, listing, activeStoreId);
  const toggleFlip = (e) => {
    e?.stopPropagation?.();
    if (canFlip) setFlipped((v) => !v);
  };

  const onCardClick = (e) => {
    if (e.target.closest("button, input, a, label, [role='button'], [role='menuitem']")) return;
    if (e.target.closest(".ft-lcard__photo img")) {
      onNavigate(e);
      return;
    }
    if (canFlip) toggleFlip(e);
  };

  const actionItems = [
    {
      label: "Record Sale",
      onSelect: onRecordSale,
      disabled: storeGuardBlocked,
      hint: storeGuardBlocked ? storeGuardReason : undefined,
    },
    canShare ? { label: "Send via WhatsApp", onSelect: onBroadcast } : null,
    wa.canSend(listing)
      ? {
          label: "Share to Broadcast",
          onSelect: (e) => {
            e?.stopPropagation?.();
            wa.open(listingSubject(listing));
          },
        }
      : null,
    {
      label: ebayPolicyFlagged ? "Clear eBay flag" : "Mark as eBay-flagged",
      onSelect: onToggleEbayFlag,
    },
    { label: "Delete", onSelect: onDelete, danger: true },
  ].filter(Boolean);

  // Guarded HERE, below every hook, and not at the top of the component: an
  // early return above the hooks means a card whose `listing` prop flips
  // truthy/falsy mid-life renders a different NUMBER of hooks, and React kills
  // the whole list with "rendered fewer hooks than expected". Every read above
  // is optional-chained for the same reason.
  if (!listing) return null;

  const marketplacePanel = (
    <div
      className="rc-lcard-adapter__display-panel"
      role="dialog"
      aria-label="Listing marketplaces"
      onClick={(event) => event.stopPropagation()}
    >
      {mpAccountsLoading ? (
        <div className="rc-lcard-adapter__display-row is-muted">Loading marketplaces…</div>
      ) : null}
      {!mpAccountsLoading && enabledMpAccounts.length === 0 ? (
        <div className="rc-lcard-adapter__display-row is-muted">
          No marketplace accounts in Settings
        </div>
      ) : null}
      {!mpAccountsLoading && enabledMpAccounts.map((acc) => {
        if (acc.marketplace === "ebay") {
          return (
            <div key={acc.id} className="rc-lcard-adapter__display-row">
              <MarketplaceIcon marketplace="ebay" label={acc.label || "eBay"} />
              <span className="rc-lcard-adapter__display-name">{acc.label || "eBay"}</span>
              <span className="rc-lcard-adapter__market-spacer" />
              <RCToggle checked={ebay} onChange={onEbayChange} />
            </div>
          );
        }
        if (acc.marketplace === "chrono24") {
          return (
            <Chrono24ToggleRow
              key={acc.id}
              listing={listing}
              account={acc}
              on={!!chronoState[acc.id]}
              syncStatus={chronoSyncState[acc.id]}
              onStateChange={reportChronoState}
            />
          );
        }
        if (acc.marketplace === "watchfinder") {
          const wfReview = marketplaceReviewLabel(watchfinder, wfState?.sync_status);
          const showWfError = () => alert.show(
            wfErrorReason.length
              ? `WatchFinder rejected this listing:\n• ${wfErrorReason.join("\n• ")}`
              : "WatchFinder rejected this listing but didn't return a reason."
          );
          return (
            <div key={acc.id} className="rc-lcard-adapter__display-row">
              <MarketplaceIcon marketplace="watchfinder" label={acc.label || "WatchFinder"} />
              <div className="rc-lcard-adapter__display-copy">
                <span className="rc-lcard-adapter__display-name">{acc.label || "WatchFinder"}</span>
                {wfReview ? <span className="rc-lcard-adapter__display-status">{wfReview}</span> : null}
              </div>
              {wfPill ? <Badge tone={wfPill.tone} size="sm">{wfPill.label}</Badge> : null}
              {wfPill?.tone === "danger" ? (
                <Tooltip
                  content={
                    wfErrorReason.length
                      ? `WatchFinder rejected: ${wfErrorReason.join(" · ")}`
                      : "Rejected by WatchFinder — no reason provided."
                  }
                >
                  <IconButton
                    variant="ghost"
                    size="sm"
                    label="Why WatchFinder rejected this listing"
                    data-btn="listing-wf-reason"
                    onClick={showWfError}
                  >
                    <Icon name="Info" size="sm" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <span className="rc-lcard-adapter__market-spacer" />
              <RCToggle checked={watchfinder} onChange={onWatchfinderChange} />
            </div>
          );
        }
        return null;
      })}

      <div className="rc-lcard-adapter__display-row is-internal">
        <span className="rc-lcard-adapter__display-name">In Stock</span>
        <span className="rc-lcard-adapter__market-spacer" />
        <RCToggle
          checked={inStock}
          onChange={onDaoChange}
          variant="active"
          disabled={ownershipLocked}
          title={ownershipLocked ? ownershipLockNote : undefined}
        />
      </div>
      <div className="rc-lcard-adapter__display-row">
        <span className="rc-lcard-adapter__display-name">Consignment</span>
        <span className="rc-lcard-adapter__market-spacer" />
        <RCToggle
          checked={consignment}
          onChange={onConsignmentChange}
          variant="active"
          disabled={ownershipLocked}
          title={ownershipLocked ? ownershipLockNote : undefined}
        />
      </div>
      {/* The value neither toggle above can show, said instead of letting a
          click quietly overwrite it (lost in the Fontaine card adoption). */}
      {ownershipLocked ? (
        <div className="rc-lcard-adapter__display-row">
          <span className="rc-lcard-adapter__display-name">Owned By</span>
          <span className="rc-lcard-adapter__market-spacer" />
          <span className="rc-lcard-adapter__display-status" title={ownershipLockNote}>
            To Be Procured
          </span>
        </div>
      ) : null}
    </div>
  );

  const card = (
    <ListingCardCell className="rc-lcard-adapter__cell">
      {wa.sheet}
      {enabledMpAccounts
        .filter((account) => account.marketplace === "chrono24" && account.slug !== null)
        .map((account) => (
          <Chrono24Hydrator
            key={account.id}
            listingId={listing.id}
            accountId={account.id}
            onStateChange={reportChronoHydration}
          />
        ))}
      <ListingCard
        className="rc-lcard-adapter"
        brand={brand}
        reference={ref}
        model={model}
        price={price}
        currency={currency}
        year={year || "—"}
        condition={condition || "—"}
        image={image}
        imageAlt={model}
        marketplaceCount={
          mpAccountsLoading ? "—/—" : marketplaceTotal > 0 ? `${marketplaceCount}/${marketplaceTotal}` : null
        }
        active={active}
        reserved={reserved}
        onActiveChange={onActiveChange}
        onReservedChange={onReservedChange}
        // No open button: clicking the photo already opens the listing
        // (see onCardClick), so an arrow beside the kebab was a second way in.
        onMarketplaces={(event) => {
          // Stops the card's own click handler AND the document dismissal above.
          event.stopPropagation();
          setDisplayOpen((value) => !value);
        }}
        marketplaceOpen={displayOpen}
        flipped={flipped}
        onFlippedChange={setFlipped}
        onClick={onCardClick}
        title={canFlip ? (flipped ? "Show listing" : "Show internal details") : undefined}
        slots={{
          // Auto-created from a sell/order request and never opened by the store
          // (BE clears listings.needs_review when the detail/edit page opens).
          priceAdornment: needsReview ? (
            <Tooltip content="Listing info was never reviewed">
              <span
                className="rc-lcard-adapter__review-dot"
                tabIndex={0}
                role="img"
                aria-label="Listing info was never reviewed"
              />
            </Tooltip>
          ) : null,
          photoOverlay: (
            <div className="rc-lcard-adapter__photo-overlay">
              <div className="rc-lcard-adapter__badges">
                {!mpAccountsLoading && wfAccount && active &&
                  (!watchfinder || wfState?.sync_status === "error") ? (
                    <Tooltip content={wfBadgeTip} side="bottom">
                      {/* tabIndex makes this reachable by keyboard, which is
                          also what lets Tooltip open on focus — a bare span
                          would only ever respond to a hovering mouse. */}
                      <span
                        className="rc-lcard-adapter__badge is-wf"
                        tabIndex={0}
                        role="note"
                        aria-label={wfBadgeTipText}
                      >
                        WF
                      </span>
                    </Tooltip>
                  ) : null}
                {listing.likeCount ? (
                  <span className="rc-lcard-adapter__badge is-like" aria-label={`${listing.likeCount} likes`}>
                    {listing.likeCount}
                  </span>
                ) : null}
                {ebayPolicyFlagged ? (
                  <span
                    className="rc-lcard-adapter__badge is-ebay"
                    title="Previously blocked by eBay for a policy violation"
                  >
                    eBay
                  </span>
                ) : null}
                {consignment ? <span className="rc-lcard-adapter__badge is-consignment">Consignment</span> : null}
                {inRepair ? <span className="rc-lcard-adapter__badge is-repair">In Repair</span> : null}
              </div>
              <div className="rc-lcard-adapter__photo-actions">
                <IconButton
                  className="ft-lcard__icon"
                  size="sm"
                  label="Edit"
                  disabled={storeGuardBlocked}
                  title={storeGuardReason || "Edit"}
                  onClick={onEdit}
                >
                  <Icon name="Pencil" size="xs" />
                </IconButton>
                {/* Kebab, not an arrow: the card already renders an arrow and
                    that one opens the listing. This opens the actions menu. */}
                <Menu
                  label="Listing actions"
                  items={actionItems}
                  trigger={(
                    <IconButton className="ft-lcard__icon" size="sm" label="Actions">
                      <Icon name="MoreHorizontal" size="xs" />
                    </IconButton>
                  )}
                />
              </div>
            </div>
          ),
          backFace: canFlip ? <ListingInternalFace listing={listing} /> : null,
          marketplacePanel: displayOpen ? marketplacePanel : null,
        }}
      />

      <EbayPolicyModal
        isOpen={!!ebayWarnVariant}
        variant={ebayWarnVariant}
        restrictedUntil={ebayRestrictedUntil}
        onConfirm={onEbayWarnConfirm}
        onCancel={onEbayWarnCancel}
      />
      <DuplicateListingModal
        isOpen={showDuplicate}
        busy={duplicating}
        onYes={onDuplicateYes}
        onNo={onDuplicateNo}
      />
    </ListingCardCell>
  );

  // Price-review mode (a brand banner's View): the price check pills sit in a
  // strip ABOVE the card, outside it. The strips in a grid row stretch to the
  // tallest, so the cards stay aligned and the pills hug each card's top edge.
  return priceReviewMode ? (
    <div className="rc-lcard-adapter__review">
      <div className="rc-lcard-adapter__review-strip">
        <PriceCheckChips listing={listing} />
      </div>
      {card}
    </div>
  ) : card;
};


/**
 * One Chrono24 toggle row inside the marketplace dropdown panel.
 *
 * Legacy DAO-UK (slug=null) reads listing.is_chrono directly (no extra fetch).
 * Non-legacy accounts hydrate via the per-listing per-account API and react-
 * query caches each (listingId, accountId) so multiple cards on /rc/listings
 * deduplicate requests. The state is reported up to the parent card so the
 * "n/total" counter stays in sync.
 *
 * Toggle PUT always hits /api/listings/{id}/marketplaces/{accountId} —
 * the backend dual-writes listings.is_chrono for the legacy slug=null row,
 * keeping the existing /chrono24-feed read path correct.
 */
const Chrono24Hydrator = ({ listingId, accountId, onStateChange }) => {
  const { state } = useListingMarketplaceState(listingId, accountId, { enabled: true });
  useEffect(() => {
    if (state) onStateChange(accountId, state);
  }, [accountId, onStateChange, state]);
  return null;
};

const Chrono24ToggleRow = ({ listing, account, on, syncStatus, onStateChange }) => {
  const { showApiError } = useApiError();
  // `on` is owned by the parent (the Hydrator above hydrates it for non-legacy
  // rows; legacy rows seed from listing.is_chrono). The row still subscribes to
  // the per-account state itself, for two things the parent does not carry:
  // `publish_state`, and a refetch handle for the PUT below. Same react-query
  // key as the Hydrator, so a non-legacy row shares that cache instead of
  // adding a request. Legacy rows never hydrated — publish_state lives on the
  // junction row for them too, so they fetch here, and only when there is
  // actually a status to show (toggle on + a monitorable account). Same shape
  // as the WatchFinder pill's guard, and it adds no request to an off or
  // unmonitored row.
  const isLegacy = account.slug === null;
  const { state: fetched, refetch: refetchState } = useListingMarketplaceState(
    listing.id,
    account.id,
    { enabled: !isLegacy || ch24ShouldFetchState(account, on) }
  );

  const onChange = async (val) => {
    onStateChange(account.id, val);
    try {
      await axios.put(
        `/api/listings/${listing.id}/marketplaces/${account.id}`,
        { is_listed: val },
        SILENT_AXIOS
      );
      // The PUT resets publish_state server-side (on => pending/orange, off =>
      // unmonitored), so the cached row is now stale. Without this refetch the
      // toggle would go green-ish/blank until the 60s staleTime expired, which
      // is exactly the moment the merchant is looking at it.
      if (ch24ShouldFetchState(account, val)) refetchState();
    } catch (error) {
      onStateChange(account.id, !val);
      showApiError(error, `update the ${account.label} listing`);
    }
  };

  // Live publish status. Orange = in the feed, not yet seen on Chrono24;
  // green = actually seen. Shared with the listing form's tab dots so the two
  // surfaces cannot disagree — see helpers/chrono24PublishState.js.
  const publish = ch24PublishPresentation({ account, isListed: on, state: fetched });
  // Prefer this row's own fetch; fall back to the sync status the Hydrator
  // reported up, which is all a disabled query has.
  const reviewLabel = marketplaceReviewLabel(on, fetched?.sync_status ?? syncStatus);

  return (
    <div className="rc-lcard-adapter__display-row">
      <MarketplaceIcon marketplace="chrono24" label={account.label} />
      <div className="rc-lcard-adapter__display-copy">
        <span className="rc-lcard-adapter__display-name">{account.label || "Chrono 24"}</span>
        {reviewLabel && (
          <span className="rc-lcard-adapter__display-status">{reviewLabel}</span>
        )}
      </div>
      <div className="rc-lcard-adapter__market-spacer" />
      {publish.pill && (
        <span
          className={`rc-lcard-adapter__mp-pill is-${publish.pill.tone}`}
          title={publish.pill.title}
        >
          {publish.pill.label}
        </span>
      )}
      <RCToggle
        checked={on}
        onChange={onChange}
        variant={publish.tone}
        title={publish.pill?.title}
      />
    </div>
  );
};

export default RCListingCard;
